import { ApplicationController, useDebounce } from "stimulus-use";
import { Editor } from "@tiptap/core";
import { DirectUpload } from "@rails/activestorage";
import { StarterKit } from "@tiptap/starter-kit";
import { TextAlign } from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { Table, TableHeader, TableRow } from "@tiptap/extension-table";
import TableCellExtend from "./advanced_editor/table_cell_extend.js";
import { Highlight } from "@tiptap/extension-highlight";
import ImageExtended from "./advanced_editor/image_extended.ts";
import HeadingExtended from "./advanced_editor/heading_extended.js";
import ParagraphExtended from "./advanced_editor/paragraph_extended.js";
import LinkExtended from "./advanced_editor/link_extended.js";
import SearchAndReplace from "./advanced_editor/search_and_replace.js";
import { Color } from "@tiptap/extension-color";

export default class extends ApplicationController {
  static values = {
    isEnabled: Boolean,
    autoSave: Boolean,
    autofocus: Boolean,
    hasTable: Boolean,
    hasContent: Boolean,
    allowTableModification: Boolean,
    stylePreferences: Object,
    preventTexts: Object
  };

  static targets = [
    "editorContainer",
    "content",
    "undoButton",
    "redoButton",
    "headingsButtons",
    "tableButtons",
    "toggleBoldButton",
    "toggleUnderlineButton",
    "toggleItalicButton",
    "changeColorButton",
    "tableRow",
    "plusTableButton",
    "minusTableButton",
    "imageForm",
    "addLinkButton",
    "addLinkTemplate",
    "showLinkTemplate",
    "scrollContainer",
    "listButtons",
    "alignementButtons",
    "reinitializeButton",
    "metaSubmit",
    "searchBar",
    "searchInput",
    "replaceInput",
    "matchCounter",
    "replaceButton",
    "replaceAllButton",
    "toggleSearchButton"
  ];

  static debounces = ["updateHTML", { name: "search", wait: 200 }];

  connect() {
    const tableRowActions =
      this.allowTableModificationValue ?
        "mouseenter->advanced-editor#handleHoverTableRow mouseout->advanced-editor#handleRowMouseLeave" :
        undefined;
    this.editor = new Editor({
      element: this.editorContainerTarget,
      extensions: [
        StarterKit.configure({
          heading: false,
          paragraph: false,
          link: false,
        }),
        Table.configure({
          resizable: this.allowTableModificationValue,
          cellMinWidth: 150,
          allowTableNodeSelection: true,
        }),
        TableHeader,
        TableRow.configure({
          HTMLAttributes: {
            "data-action": tableRowActions
          },
        }),
        TableCellExtend.configure({
          allowTableModification: this.allowTableModificationValue,
        }),
        TextStyle,
        Color,
        ImageExtended,
        HeadingExtended.configure({
          levels: [1, 2, 3],
          stylePreferences: this.stylePreferencesValue,
        }),
        ParagraphExtended.configure({
          stylePreferences: this.stylePreferencesValue,
        }),
        LinkExtended.configure({
          defaultProtocol: "https",
          stylePreferences: this.stylePreferencesValue,
          HTMLAttributes: {
            "data-action":
              "mouseover->advanced-editor#showExistingLink mouseout->advanced-editor#hideExistingLink",
          },
        }),
        Highlight.configure({
          multicolor: true,
        }),
        TextAlign.configure({
          types: ["heading", "paragraph"],
          defaultAlignment: "left",
        }),
        SearchAndReplace,
      ],
      onUpdate: this.onUpdate,
      onFocus: this.onFocus,
      onSelectionUpdate: this.onSelectionUpdate,
      content: this.contentTarget.value,
    });

    this.#setMenuButtonsStates();
    this.#toggleLinkActivation();

    this.saveStatusBadge = document.getElementById("save-status-badge");

    useDebounce(this, { wait: 2000 });

    if (this.autofocusValue) {
      this.editor.commands.focus();
    }

    if (this.hasTableValue) {
      this.#setColumnButtonsState();
      if (!this.hasContentValue) {
        this.#insertTable();
      }
    } else {
      const tables = this.editorContainerTarget.querySelectorAll("table");
      tables.forEach((table) => {
        table.addEventListener("mouseenter", this.handleTable);
      });
    }

    if (!this.isEnabledValue) {
      this.editor.setEditable(false);
    }
  }

  onUpdate = ({ editor }) => {
    if (!this.isEnabledValue) return;

    if (!this.autoSaveValue) {
      this.#resetTable();
      this.#enableSubmit();
      return;
    }

    const json = this.editor.getJSON();
    const html = editor.getHTML();

    const hasHighlight = this.#checkForHighlight(json);
    if (hasHighlight || this.contentTarget.value === html) return;

    this.#setMenuButtonsStates();
    this.saveStatusBadge.dataset.saved = false;
    this.saveStatusBadge.classList.remove("hidden");
    this.updateHTML(html);
  };

  #checkForHighlight(json) {
    const checkNode = (node) => {
      if (node.type === "text") {
        return (
          node.marks && node.marks.some((mark) => mark.type === "highlight")
        );
      }
      return node.content && node.content.some(checkNode);
    };

    return json.content.some(checkNode);
  }

  onFocus = ({ editor }) => {
    this.editor.setEditable(true);
    this.#toggleLinkActivation();
  };

  onSelectionUpdate = ({ editor }) => {
    this.#setMenuButtonsStates();
    this.#toggleLinkActivation();
  };

  updateHTML(html) {
    const form = this.contentTarget.form;
    this.contentTarget.value = html;
    form.requestSubmit();
    this.saveStatusBadge.dataset.saved = true;
    this.reinitializeButtonTarget.dataset.state = "enabled";
    setTimeout(() => {
      this.saveStatusBadge.classList.add("hidden");
    }, 2000);
  }

  disconnet() {
    this.editor.destroy();
  }

  toggleSearch() {
    if (this.searchBarTarget.classList.contains("hidden")) {
      this.toggleSearchButtonTarget.dataset.active = true
      this.searchBarTarget.classList.remove("hidden");
      this.searchInputTarget.focus();
      this.#refreshSearchUI();
    } else {
      this.toggleSearchButtonTarget.dataset.active = false
      this.closeSearch();
    }
  }

  closeSearch() {
    this.searchBarTarget.classList.add("hidden");
    this.toggleSearchButtonTarget.dataset.active = false
    this.editor.commands.clearSearch();
    this.searchInputTarget.value = "";
    this.replaceInputTarget.value = "";
    this.#refreshSearchUI();
  }

  search(event) {
    this.editor.commands.setSearchTerm(event.target.value);
    this.#refreshSearchUI();
  }

  setReplaceTerm(event) {
    this.editor.commands.setReplaceTerm(event.target.value);
  }

  nextMatch() {
    this.editor.commands.nextMatch();
    this.#refreshSearchUI();
    this.#scrollToCurrentMatch();
  }

  previousMatch() {
    this.editor.commands.previousMatch();
    this.#refreshSearchUI();
    this.#scrollToCurrentMatch();
  }

  replaceCurrent() {
    this.editor.commands.replaceCurrent();
    this.#refreshSearchUI();
  }

  replaceAll() {
    this.editor.commands.replaceAll();
    this.#refreshSearchUI();
  }

  #refreshSearchUI() {
    const { results, currentIndex } = this.editor.storage.searchAndReplace;
    const total = results.length;
    this.matchCounterTarget.textContent =
      total ? `${currentIndex + 1}/${total}` : "0/0";
    const isDisabledButton = total === 0
    this.replaceButtonTarget.disabled = isDisabledButton
    this.replaceAllButtonTarget.disabled = isDisabledButton
  }

  #scrollToCurrentMatch() {
    const { results, currentIndex } = this.editor.storage.searchAndReplace;
    const result = results[currentIndex];
    if (!result) return;

    const { node } = this.editor.view.domAtPos(result.from);
    const element =
      node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
    element?.scrollIntoView({ block: "center" });
  }

  undo() {
    this.editor.chain().focus().undo().run();
  }

  redo() {
    this.editor.chain().focus().redo().run();
  }

  changeHeading(event) {
    const level = parseInt(event.currentTarget.dataset.level);
    this.editor.chain().focus().setHeading({ level }).run();
  }

  removeHeading() {
    this.editor.chain().focus().setParagraph().run();
  }

  toggleTableColumns(event) {
    const target = event.currentTarget;
    this.cols = parseInt(target.dataset.columns, 10);
    this.rows = 3 - this.cols;

    this.element.dataset.tableColumns = this.cols;
    this.#setColumnButtonsState();
    this.#rebuildTable(this.rows, this.cols);
    this.#enableSubmit();
  }

  #extractCellContents() {
    const contents = [];

    document.querySelectorAll("td").forEach((td) => {
      contents.push(td.innerHTML);
    });

    return contents;
  }

  #rebuildTable(rows, cols) {
    const previousContents = this.#extractCellContents();

    const totalCells = rows * cols;
    const cells = [];
    for (let i = 0; i < totalCells; i++) {
      let content = previousContents[i];
      if (content === '<p><br><br class="ProseMirror-trailingBreak"></p>') {
        content = "";
      }
      cells.push(`<td>${content}</td>`);
    }

    let tableRows = "";
    for (let r = 0; r < rows; r++) {
      const start = r * cols;
      const end = start + cols;
      tableRows += `<tr>${cells.slice(start, end).join("")}</tr>`;
    }

    const html = `<table><tbody>${tableRows}</tbody></table>`;

    this.editor.commands.setContent(html, { emitUpdate: false });
  }

  #insertTable() {
    this.editor.commands.insertTable({
      rows: this.rows || 2,
      cols: this.cols || 1,
      withHeaderRow: false,
    });
  }

  #resetTable() {
    const currentHTML = this.editor.getHTML();
    const emptyHTML = "<p></p>";
    const isCurrentHTMLEmpty = currentHTML === emptyHTML;
    if (isCurrentHTMLEmpty) {
      this.#insertTable();
    }
  }

  #enableSubmit() {
    const currentHTML = this.editor.getHTML();
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = currentHTML;
    const tds = Array.from(tempDiv.querySelectorAll("td"));
    const areTdsEmpty = tds.every((td) => td.innerHTML === '<p style="text-align: left"></p>');
    this.metaSubmitTarget.disabled = areTdsEmpty;
    if (areTdsEmpty) return

    this.element.dispatchEvent(new CustomEvent("modal:lock"));
    this.element.dispatchEvent(
      new CustomEvent("modal:preventexit", {
        detail: this.preventTextsValue
      })
    );
  }

  #setColumnButtonsState() {
    const buttons = this.tableButtonsTarget.querySelectorAll("button");
    buttons.forEach((button) => {
      button.dataset.active =
        button.dataset.columns === this.element.dataset.tableColumns;
    });
  }

  toggleBold() {
    this.editor.chain().focus().toggleBold().run();
  }

  toggleItalic() {
    this.editor.chain().focus().toggleItalic().run();
  }

  toggleUnderline() {
    this.editor.chain().focus().toggleUnderline().run();
  }

  changeColor(event) {
    const color = event.currentTarget.value;
    const chain = this.editor.chain().focus().setColor(color);
    if (this.editor.isActive("link")) {
      chain.setMark("underline");
    }
    chain.run();
  }

  addTable() {
    const { view } = this.editor;
    const { state } = view;
    this.editor.commands.insertTable({
      rows: 3,
      cols: 2,
      withHeaderRow: false,
    });

    const cell = this.getNode();
    const table = cell.parentElement.parentElement.parentElement;
    table.addEventListener("mouseenter", this.handleTable);

    const transaction = state.tr;
    const posAfterTable = transaction.selection.$from.pos;
    this.editor.commands.focus(posAfterTable);
  }

  handleTable = (event) => {
    this.activeTable = event.currentTarget;
    this.activeRows = this.activeTable.querySelectorAll("tr");
    this.activeCells = this.activeTable.querySelectorAll("td");

    const resizeObserver = new ResizeObserver(() => {
      this.updateElementPosition();
    });

    resizeObserver.observe(this.activeTable);
  };

  updateElementPosition() {
    const addColButton = document.querySelector(
      'button[data-action="addTableCol"]',
    );
    if (this.activeTable && addColButton) {
      const tableRect = this.activeTable.getBoundingClientRect();
      if (tableRect.width >= 749) {
        this.editor.commands.fixTables();
      }
      addColButton.style.left = `${tableRect.right + 10}px`;
    }
  }

  handleHoverTableRow(event) {
    const existingAddRowButton = document.querySelector(
      'button[data-action="addTableRow"]',
    );
    if (this.hasCellFocused || existingAddRowButton) return;

    const row = event.currentTarget;
    const table = row.parentElement.parentElement;
    const rowRect = row.getBoundingClientRect();
    if (this.activeRows[this.activeRows.length - 1] === row) {
      this.createPlusButton(
        "Row",
        rowRect.bottom + 10,
        table.getBoundingClientRect(),
        true,
      );
    }
  }

  handleRowMouseLeave = (event) => {
    const lastRow = this.activeRows[this.activeRows.length - 1];
    if (
      lastRow === event.currentTarget ||
      event.currentTarget.tagName === "BUTTON"
    ) {
      const existingAddRowButton = document.querySelector(
        'button[data-action="addTableRow"]',
      );
      if (!existingAddRowButton) return;

      const x = event.clientX;
      const y = event.clientY;

      const rowRect = lastRow.getBoundingClientRect();
      const buttonRect = existingAddRowButton.getBoundingClientRect();

      const left = rowRect.left;
      const right = rowRect.right;
      const top = rowRect.top;
      const bottom = buttonRect.bottom - 1;

      if (x > left && x < right && y > top && y < bottom) return;

      existingAddRowButton.remove();
      this.isAnyTableToolPresent = false;
    }
  };

  handleHoverTableCell(event) {
    const lastCell = this.activeCells[this.activeCells.length - 1];
    const rowsNumber = lastCell.cellIndex;
    const isMaxRows = rowsNumber === 4;
    const existingAddColButton = document.querySelector(
      'button[data-action="addTableCol"]',
    );
    if (this.hasCellFocused || existingAddColButton || isMaxRows) return;

    const cell = event.currentTarget;
    const tr = cell.parentElement;
    const cells = tr.children;

    if (cells[cells.length - 1] === cell) {
      this.createPlusButton(
        "Col",
        this.activeTable.getBoundingClientRect().top,
        this.activeTable.getBoundingClientRect(),
        false,
      );
    }
  }

  handleColMouseLeave = (event) => {
    const lastCell = this.activeCells[this.activeCells.length - 1];
    if (
      event.currentTarget.tagName === "BUTTON" ||
      lastCell.cellIndex === event.currentTarget.cellIndex
    ) {
      const existingAddColButton = document.querySelector(
        'button[data-action="addTableCol"]',
      );
      if (!existingAddColButton) return;

      const x = event.clientX;
      const y = event.clientY;

      const cellRect = lastCell.getBoundingClientRect();
      const buttonRect = existingAddColButton.getBoundingClientRect();

      const left = cellRect.left;
      const right = buttonRect.right - 1;
      const top = buttonRect.top;
      const bottom = buttonRect.bottom;

      if (x > left && x < right && y > top && y < bottom) return;

      existingAddColButton.remove();
      this.isAnyTableToolPresent = false;
    }
  };

  handleCellClick(event) {
    if (this.allowTableModificationValue) {
      const plusButtons = document.querySelectorAll(
        'button[data-behavior="addTableElement"]',
      );
      plusButtons.forEach((button) => {
        button.remove();
      });
      const minusButtons = document.querySelectorAll(
        'button[data-behavior="removeTableElement"]',
      );
      minusButtons.forEach((button) => {
        button.remove();
      });

      const cell = event.currentTarget;

      this.hasCellFocused = true;

      const row = cell.parentElement;
      const table = row.parentElement.parentElement;

      const cellRect = cell.getBoundingClientRect();
      const rowRect = row.getBoundingClientRect();
      const tableRect = table.getBoundingClientRect();

      const colTopPos = tableRect.top - 42;
      const colLeftPos = cellRect.left;
      const colWidth = cellRect.width;
      this.createMinusButton("Col", colTopPos, colLeftPos, colWidth);

      const rowTopPos = cellRect.top;
      const rowLeftPos = rowRect.left - 42;
      this.createMinusButton("Row", rowTopPos, rowLeftPos);
    }
  }

  createPlusButton(tableElementType, top, tableRect, isRow) {
    this.isAnyTableToolPresent = true;
    const plusTemplate = document.getElementById("plus-table-template");
    const plusClone = plusTemplate.content.cloneNode(true);
    document.body.appendChild(plusClone);
    const button = document.body.lastElementChild;

    button.addEventListener("click", this[`addTable${tableElementType}`]);
    button.addEventListener(
      "mouseleave",
      this[`handle${tableElementType}MouseLeave`],
    );
    button.dataset.action = `addTable${tableElementType}`;

    button.style.top = `${top}px`;
    button.style.left = `${isRow ? tableRect.left : tableRect.right + 10}px`;

    if (isRow) {
      button.style.width = `${tableRect.width}px`;
    } else {
      button.style.height = `${tableRect.height}px`;
    }
  }

  addTableRow = (event) => {
    event.currentTarget.remove();

    const pos = this.editor.view.posAtDOM(
      this.activeCells[this.activeCells.length - 1],
      0,
    );
    this.editor.commands.focus(pos);

    this.editor.commands.addRowAfter();

    setTimeout(() => {
      const newPos = this.editor.view.posAtDOM(
        this.activeRows[this.activeRows.length - 1].children[0],
        0,
      );
      this.editor.commands.focus(newPos);
    }, 0);
  };

  addTableCol = () => {
    event.currentTarget.remove();

    const pos = this.editor.view.posAtDOM(
      this.activeCells[this.activeCells.length - 1],
      0,
    );
    this.editor.commands.focus(pos);

    this.editor.commands.addColumnAfter();

    setTimeout(() => {
      const newPos = this.editor.view.posAtDOM(
        this.activeRows[0].children[this.activeRows[0].children.length - 1],
        0,
      );
      this.editor.commands.focus(newPos);
    }, 0);
  };

  createMinusButton(tableElementType, top, left, width) {
    this.isAnyTableToolPresent = true;
    const minusTemplate = document.getElementById("minus-table-template");
    const minusClone = minusTemplate.content.cloneNode(true);
    document.body.appendChild(minusClone);
    const button = document.body.lastElementChild;

    button.addEventListener("click", this[`removeTable${tableElementType}`]);

    button.style.top = `${top}px`;
    button.style.left = `${left}px`;
    if (width) {
      button.style.width = `${width}px`;
    }
  }

  handleCellBlur(event) {
    if (!this.editorContainerTarget.contains(event.target)) {
      const addButtons = document.querySelectorAll(
        'button[data-behavior="addTableElement"]',
      );
      addButtons.forEach((button) => {
        if (button.contains(event.target)) {
          return;
        }
        button.remove();
      });
      this.resetTableState(event.target);
      return;
    }

    if (this.hasCellFocused) {
      const element = this.getNode();
      if (element.tagName !== "TD") {
        this.resetTableState();
      }
    }
  }

  getNode() {
    const { state, view } = this.editor;
    const { selection } = state;
    const { $from } = selection;
    const pos = $from.before($from.depth);
    const { node, pos: domPos } = view.domAtPos(pos);
    return node;
  }

  resetTableState(eventTarget) {
    const minusButtons = document.querySelectorAll(
      'button[data-behavior="removeTableElement"]',
    );
    minusButtons.forEach((button) => {
      if (eventTarget && button.contains(eventTarget)) {
        return;
      }
      button.remove();
    });
    this.hasCellFocused = false;
    this.isAnyTableToolPresent = false;
  }

  removeTableRow = (event) => {
    this.editor.commands.deleteRow() || this.editor.commands.deleteTable();
    event.currentTarget.remove();
    this.hasCellFocused = false;
    this.isAnyTableToolPresent = false;
  };

  removeTableCol = (event) => {
    this.editor.commands.deleteColumn() || this.editor.commands.deleteTable();
    event.currentTarget.remove();
    this.hasCellFocused = false;
    this.isAnyTableToolPresent = false;
  };

  removeTableToolsOnScroll() {
    if (this.allowTableModificationValue && this.isAnyTableToolPresent) {
      const tableTools = document.querySelectorAll(
        'button[data-behavior=addTableElement], button[data-behavior="removeTableElement"]',
      );
      tableTools.forEach((button) => {
        button.remove();
      });
      this.hasCellFocused = false;
      this.isAnyTableToolPresent = false;
    }
  }

  addImage(event) {
    const input = event.target;
    if (!input.files.length) return;

    [...input.files].forEach((file) => {
      this.#uploadAndInsertImage(file, event.currentTarget);
    });

    input.value = null;
  }

  handleDrop(event) {
    event.preventDefault();
    const files = event.dataTransfer.files;
    Array.from(files).forEach((file) => {
      this.#uploadAndInsertImage(file, this.imageFormTarget);
    });
  }

  #uploadAndInsertImage(file, form) {
    const upload = new DirectUpload(
      file,
      "/rails/active_storage/direct_uploads",
    );

    const fileName = file.name
      .split(".")
      .slice(0, -1)
      .join(".")
      .replace(/[_-]/g, " ");

    upload.create((error, blob) => {
      if (error) {
        console.error("Direct upload failed:", error);
        return;
      }

      const imageUrl = `/rails/active_storage/blobs/${blob.signed_id}/${blob.filename}`;
      this.#insertImage(imageUrl, fileName);
    });
  }

  #insertImage(imageUrl, fileName) {
    const imageNode = {
      type: "image",
      attrs: {
        src: imageUrl,
        title: "",
      },
    };
    this.editor
      .chain()
      .focus()
      .insertContent(imageNode)
      .createParagraphNear()
      .run();
  }

  addLink({ href, initialLink }) {
    const template = this.addLinkTemplateTarget;
    const clone = template.content.cloneNode(true);

    const { from, to } = this.editor.state.selection;
    const selectedNode = this.editor.state.doc.nodeAt(from);

    if (from === to && !initialLink) {
      alert("Veuillez sélectionner un texte");
      return;
    }

    this.hasLink = true;
    this.isAddLinkRunning = true;
    this.linkInput = clone.getElementById("link-input");
    this.linkInput.value = href || "";
    this.submitLinkButton = clone.getElementById("link-submit-button");
    this.cancelLinkButton = clone.getElementById("link-cancel-button");

    this.currentNode = initialLink || this.editor.view.domAtPos(from).node;

    document.body.appendChild(clone);
    this.linkContainer = document.getElementById("link-container");

    this.#updateLinkContainerPosition({ from, to, target: initialLink });
    if (this.hasScrollContainerTarget) {
      this.scrollContainerTarget.dataset.locked = true;
    }

    this.editor.chain().focus().toggleHighlight({ color: "#86efac" }).run();

    setTimeout(() => {
      if (href) {
        this.linkInput.select();
      } else {
        this.linkInput.focus();
      }
    }, 0);
    this.linkInput.addEventListener("input", (event) => {
      this.#verifySubmitEnabling(event);
    });
    this.cancelLinkButton.addEventListener("click", () => {
      this.#cancelUrl();
    });
    this.submitLinkButton.addEventListener("click", () => {
      this.#submitUrl({ link: initialLink });
    });

    this.#addEventListenersAfterDelay();
  }

  #updateLinkContainerPosition({ from, to, target }) {
    if (this.element.id == "modal-content") {
      this.element.removeAttribute("data-action");
    }

    const selectionRect = target
      ? target.getBoundingClientRect()
      : this.editor.view.coordsAtPos(from);
    const cloneHeight = this.linkContainer.offsetHeight;
    const viewportHeight = window.innerHeight;
    const spaceBelowBottom = viewportHeight - selectionRect.bottom;
    const cloneWidth = this.linkContainer.offsetWidth;
    const viewportWidth = window.innerWidth;
    const spaceBelowRight = viewportWidth - selectionRect.right;
    const selectionRightPosition = target
      ? selectionRect.right
      : this.editor.view.coordsAtPos(to).right;

    if (spaceBelowBottom < cloneHeight) {
      this.linkContainer.style.top = `${selectionRect.top - cloneHeight - 5}px`;
    } else {
      this.linkContainer.style.top = `${selectionRect.bottom + 5}px`;
    }
    if (spaceBelowRight < cloneWidth) {
      this.linkContainer.style.left = `${selectionRightPosition - cloneWidth}px`;
    } else {
      this.linkContainer.style.left = `${selectionRect.left}px`;
    }
  }

  async #addEventListenersAfterDelay() {
    await new Promise((resolve) => {
      setTimeout(resolve, 0);
    });

    this.handleClickOutside = (event) => {
      if (!this.linkContainer.contains(event.target)) {
        this.#cancelUrl();

        const markedElement = document.querySelector("mark");
        markedElement.outerHTML = markedElement.textContent;
      }
    };

    this.handleEscapeKey = (event) => {
      if (event.key === "Escape") {
        this.#cancelUrl();
      }
    };

    window.addEventListener("click", this.handleClickOutside);
    window.addEventListener("keydown", this.handleEscapeKey);
  }

  #verifySubmitEnabling(event) {
    const value = event.currentTarget.value;
    const urlPattern = /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(\/[^\s]*)?$/i;
    const isValidUrl = urlPattern.test(value);
    this.submitLinkButton.disabled = !isValidUrl;
  }

  #submitUrl({ link }) {
    let url = this.linkInput.value.trim();
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      url = "https://" + url;
    }

    if (link) {
      link.href = url;
    } else {
      this.editor.commands.setLink({ href: url });
    }
    this.#cancelUrl();
  }

  #cancelUrl() {
    this.hasLink = false;
    this.isAddLinkRunning = false;
    window.removeEventListener("click", this.handleClickOutside);
    window.removeEventListener("keydown", this.handleEscapeKey);
    if (this.hasScrollContainerTarget) {
      this.scrollContainerTarget.dataset.locked = false;
    }
    this.editor.commands.unsetHighlight();
    this.linkContainer.remove();
  }

  showExistingLink(event) {
    if (this.hasLink) return;

    this.hasLink = true;
    const initialLink = event.currentTarget;
    const href = initialLink.href;
    const template = this.showLinkTemplateTarget;
    const clone = template.content.cloneNode(true);
    clone.querySelector("p").textContent = href;
    document.body.appendChild(clone);
    this.linkContainer = document.getElementById("show-link-container");
    this.linkContainer.addEventListener("mouseleave", (e) => {
      if (this.hasScrollContainerTarget) {
        this.scrollContainerTarget.dataset.locked = false;
      }
      e.currentTarget.remove();
    });

    const modifyButton = this.linkContainer.querySelector(
      "#modify-link-button",
    );
    modifyButton.addEventListener("click", () => {
      this.linkContainer.remove();
      this.addLink({ href, initialLink });
    });

    const removeButton = this.linkContainer.querySelector(
      "#remove-link-button",
    );
    removeButton.addEventListener("click", () => {
      if (this.hasScrollContainerTarget) {
        this.scrollContainerTarget.dataset.locked = false;
      }

      this.hasLink = false;

      const pos = this.editor.view.posAtDOM(initialLink, 0);
      this.editor.commands.focus(pos);

      this.editor.commands.unsetLink();
      this.linkContainer.remove();
    });

    this.#updateLinkContainerPosition({ target: event.currentTarget });
    if (this.hasScrollContainerTarget) {
      this.scrollContainerTarget.dataset.locked = true;
    }
  }

  hideExistingLink(event) {
    if (this.isAddLinkRunning) return;

    const rect = this.linkContainer.getBoundingClientRect();
    const padding = 10;

    if (
      event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top - padding ||
      event.clientY > rect.bottom + padding
    ) {
      if (this.hasScrollContainerTarget) {
        this.scrollContainerTarget.dataset.locked = false;
      }
      this.hasLink = false;
      this.linkContainer.remove();
    }
  }

  toggleBulletList() {
    this.editor.chain().focus().toggleBulletList().run();
  }

  toggleOrderedList() {
    this.editor.chain().focus().toggleOrderedList().run();
  }

  setTextAlignement(event) {
    if (this.#isNodeImage()) return;
    const alignement = event.currentTarget.dataset.alignement;
    this.editor.chain().focus().setTextAlign(alignement).run();
  }

  #isNodeImage() {
    const { state } = this.editor;

    const { from } = state.selection;
    const selectedNode = state.doc.nodeAt(from);
    const nodeType = selectedNode.type.name;
    return nodeType === "image";
  }

  #setMenuButtonsStates() {
    if (this.isEnabledValue) {
      this.#setUndoAndRedoStates();
      this.#setTextLevelState();
      this.#setTextDecorationState();
      this.#setColorInputValue();
      if (this.hasListButtonsTarget) {
        this.#setListState();
      }
      this.#setTextAlignementState();
    }
  }

  #setUndoAndRedoStates() {
    this.undoButtonTarget.dataset.disabled = !this.editor.can().undo();
    this.redoButtonTarget.dataset.disabled = !this.editor.can().redo();
  }

  #setTextLevelState() {
    const headingLevel = this.editor.getAttributes("heading").level;
    let level;
    if (headingLevel) {
      level = `h${headingLevel}`;
    } else {
      level = "p";
    }
    this.headingsButtonsTarget.dataset.currentLevel = level;
  }

  #setTextDecorationState() {
    this.toggleBoldButtonTarget.dataset.active = this.editor.isActive("bold");
    this.toggleUnderlineButtonTarget.dataset.active =
      this.editor.isActive("underline");
    this.toggleItalicButtonTarget.dataset.active =
      this.editor.isActive("italic");
  }

  #getCurrentColorValue() {
    const { state } = this.editor;
    const { from, to } = state.selection;

    let color = null;

    state.doc.nodesBetween(from, to, (node) => {
      const mark = node.marks.find((m) => m.type.name === "textStyle");
      if (mark?.attrs?.color) {
        color = mark.attrs.color;
        return false;
      }
    });

    if (!color) return;

    return this.#rgbToHex(color);
  }

  #rgbToHex(rgb) {
    const result = /^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/.exec(rgb);
    if (!result) return rgb;

    const r = parseInt(result[1], 10);
    const g = parseInt(result[2], 10);
    const b = parseInt(result[3], 10);

    const toHex = (val) => val.toString(16).padStart(2, "0");

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  #setColorInputValue() {
    const color = this.#getCurrentColorValue();
    this.changeColorButtonTarget.value = color;
  }

  #toggleLinkActivation() {
    const { from, to } = this.editor.state.selection;
    if (from === to) {
      this.addLinkButtonTarget.classList.add("cursor-not-allowed");
      this.addLinkButtonTarget.removeAttribute("data-action");
    } else {
      this.addLinkButtonTarget.classList.remove("cursor-not-allowed");
      this.addLinkButtonTarget.dataset.action =
        "click->advanced-editor#addLink";
    }
  }

  #setListState() {
    const { state } = this.editor;
    const $from = state.selection.$from;

    let parentListType = null;

    for (let depth = $from.depth; depth > 0; depth--) {
      const node = $from.node(depth);
      if (node.type.name === 'bulletList' || node.type.name === 'orderedList') {
        parentListType = node.type.name;
        break;
      }
    }

    this.listButtonsTarget.dataset.currentListType = parentListType;
  }

  #setTextAlignementState() {
    const { state } = this.editor
    const nodeType = state.selection.$from.parent.type.name;
    const nodeAttributes = this.editor.getAttributes(nodeType);
    let fallbackAlignement;
    const areNodeAttributesEmpty = Object.keys(nodeAttributes).length === 0;
    if (!areNodeAttributesEmpty) {
      fallbackAlignement = "left"
    }
    const alignementState = nodeAttributes.textAlign || fallbackAlignement;
    this.alignementButtonsTarget.dataset.currentAlignement = alignementState;
  }

  submitMeta(event) {
    event.preventDefault();
    const input = this.contentTarget;
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = this.editor.getHTML();

    const elements = tempDiv.querySelectorAll("*");
    elements.forEach((element) => {
      Array.from(element.attributes).forEach((attr) => {
        if (attr.name.startsWith("data-")) {
          element.removeAttribute(attr.name);
        }
      });
    });

    const cols = this.cols || 1;

    const table = tempDiv.querySelector("table");
    table.dataset.columns = cols;

    const img = tempDiv.querySelector("img");
    if (img) {
      img.dataset.columns = cols;
      if (img.style.width === "100%") {
        const rect = document.querySelector("img").getBoundingClientRect();
        img.style.width = `${rect.width}px`;
        img.style.height = "auto";
      }
    }

    input.value = tempDiv.innerHTML;
    input.form.requestSubmit();
  }
}
